import type { StateCreator } from 'zustand'
import { generateId } from '../../lib/utils.js'
import {
  MAX_VISIBLE_TOASTS, loadNoticeHistory, noticeKey, recordNotice, toastDuration,
  type NoticeEntry, type NoticeFocus, type NoticeSeverity,
} from '../../lib/notifications.js'

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastSeverity = NoticeSeverity

/**
 * Action proposée dans la notification elle-même. Elle ne vit que le temps de
 * la carte : l'historique ne garde que du sérialisable (`focus`).
 */
export interface ToastAction {
  label:   string
  onClick: () => void | Promise<void>
  /** Laisse la notification ouverte après le clic. */
  keepOpen?: boolean
}

export interface Toast {
  id:       string
  message:  string
  severity: ToastSeverity
  /** Durée d'affichage en ms, toujours positive (voir `toastDuration`). */
  duration: number
  /** Déclenchements identiques regroupés sur cette carte. */
  count:    number
  /** Incrémenté à chaque regroupement : relance le minuteur de la carte. */
  bump:     number
  actions?: ToastAction[]
  focus?:   NoticeFocus
}

export interface PushToastOptions {
  /** Historique seulement, sans carte dans la pile (détail d'un contrôle). */
  silent?: boolean
  /** Élément de grille que « Voir » rouvre, depuis la carte ou l'historique. */
  focus?:  NoticeFocus
}

// ─── Slice ────────────────────────────────────────────────────────────────────

export interface ToastSlice {
  /**
   * Cartes de la pile, les plus anciennes d'abord, jamais plus de
   * `MAX_VISIBLE_TOASTS` : la plus ancienne cède sa place, son texte reste
   * dans l'historique.
   */
  toasts: Toast[]
  /** Historique persistant, le plus récent d'abord. */
  noticeHistory: NoticeEntry[]
  noticeCenterOpen: boolean

  /**
   * Ajoute une notification. Retourne l'id de la carte. Un message identique
   * déjà dans la pile n'ajoute pas de carte : il incrémente son compteur.
   * `duration` à 0 ou absente = durée par défaut de la gravité.
   */
  pushToast: (
    message: string,
    severity?: ToastSeverity,
    duration?: number,
    action?: ToastAction | ToastAction[],
    options?: PushToastOptions,
  ) => string

  dismissToast: (id: string) => void
  setNoticeCenterOpen: (open: boolean) => void
  clearNoticeHistory: () => void
}

export const createToastSlice: StateCreator<ToastSlice, [], [], ToastSlice> = (set, get) => ({
  toasts: [],
  noticeHistory: loadNoticeHistory(),
  noticeCenterOpen: false,

  pushToast: (message, severity = 'info', duration, action, options = {}) => {
    const at = new Date().toISOString()
    const actions = action ? (Array.isArray(action) ? action : [action]) : undefined
    const key = noticeKey(message, severity)
    const existing = options.silent ? undefined : get().toasts.find(t => noticeKey(t.message, t.severity) === key)
    const id = existing?.id ?? generateId()

    set(state => {
      const noticeHistory = recordNotice(state.noticeHistory, {
        id: generateId(), message, severity, at, focus: options.focus,
      })
      if (options.silent) return { noticeHistory }

      if (existing) {
        return {
          noticeHistory,
          toasts: state.toasts.map(t => t.id === existing.id
            ? { ...t, count: t.count + 1, bump: t.bump + 1, actions: actions ?? t.actions, focus: options.focus ?? t.focus }
            : t),
        }
      }
      const toast: Toast = {
        id, message, severity,
        duration: toastDuration(severity, duration),
        count: 1, bump: 0, actions, focus: options.focus,
      }
      return { noticeHistory, toasts: [...state.toasts, toast].slice(-MAX_VISIBLE_TOASTS) }
    })
    return id
  },

  dismissToast: (id) => {
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }))
  },

  setNoticeCenterOpen: (open) => {
    set(state => ({
      noticeCenterOpen: open,
      // Ouvrir la cloche vaut lecture : la pastille retombe à zéro.
      noticeHistory: open && state.noticeHistory.some(entry => !entry.read)
        ? state.noticeHistory.map(entry => entry.read ? entry : { ...entry, read: true })
        : state.noticeHistory,
    }))
  },

  clearNoticeHistory: () => set({ noticeHistory: [] }),
})
